import React, { FC, useState } from "react";
import cx from "classnames";
import DOMPurify from "dompurify";
import { useTranslation } from "i18n";
import { dataTestIds } from "data-testids";
import { ReactComponent as ChevronDown } from "component-library/src/images/svg/icons/chevroncircle.svg";
import { ReactComponent as TBCIcon } from "component-library/src/images/svg/icons/calendar-tbc.svg";
import { IAvailabilityRecord } from "models/suggestion";
import { formatCalendarDateTime } from "components/MakeSuggestionsView/Card/TagminAvailabilityCalendar/CalendarRecord/utils/formatCalendarDateTime";

interface Props {
  record: IAvailabilityRecord;
}

const CalendarRecord: FC<React.PropsWithChildren<Props>> = ({ record }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const hasToggle = record.availability.length > 0 || record?.notes;

  const toggleIsOpen = () => setIsOpen(!isOpen);

  return (
    <div
      className="p-[5px] border-l-4 border-b border-b-color-neutral-three"
      style={{
        borderLeftColor: `#${record.colour}`,
      }}
      data-testid={
        dataTestIds.nuJobsApp[
          "MakeSuggestionsView.Card.TagminAvailabilityCalendar.CalendarRecord.individualRecord"
        ]
      }
    >
      <div className="flex items-center justify-between">
        <div className="text-xs">
          <div className="font-semibold flex flex-wrap gap-[4px]">
            {formatCalendarDateTime({
              startDateUtc: record.startDateUtc,
              endDateUtc: record.endDateUtc,
              allDay: record.allDay,
            })}
            {record.toBeConfirmed && (
              <span className="font-normal text-xxs text-orange-burnt flex items-center gap-[3px]">
                <TBCIcon />
                {t("agent:tagminAvailabilityCalendar.toBeConfirmedAb")}
              </span>
            )}
          </div>

          <div>
            <span className="font-semibold uppercase">{record.type}</span>{" "}
            {record.title} {record.category && `(${record.category})`}
          </div>
        </div>
        {hasToggle && (
          <div className="w-[18px]">
            <ChevronDown
              onClick={toggleIsOpen}
              className={cx("cursor-pointer", { "rotate-180": isOpen })}
              data-testid={
                dataTestIds.nuJobsApp[
                  "MakeSuggestionsView.Card.TagminAvailabilityCalendar.CalendarRecord.groupedRecordToggler"
                ]
              }
            />
          </div>
        )}
      </div>
      {hasToggle && isOpen && (
        <>
          {record.availability.map((availability) => {
            return (
              <div
                className="pl-[10px] text-[10px]"
                key={availability.externalId}
              >
                <div className="font-semibold">
                  {availability.title}{" "}
                  {availability.type && `(${availability.type})`}
                </div>
                <div>
                  {formatCalendarDateTime({
                    startDateUtc: availability.startDateUtc,
                    endDateUtc: availability.endDateUtc,
                    allDay: availability.allDay,
                  })}
                </div>
              </div>
            );
          })}
          <p
            className="text-xs"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(record?.notes || ""),
            }}
          />
        </>
      )}
    </div>
  );
};

export default CalendarRecord;
